import { z } from "zod";
const amount = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,30})(?:\.\d{1,80})?$/)
    .max(112);
const positive = amount.refine((value) => /[1-9]/.test(value), "Principal must be positive");
const fraction = z.string().regex(/^(?:0(?:\.\d{1,18})?|1(?:\.0{1,18})?)$/);
const belowOne = z.string().regex(/^0(?:\.\d{1,18})?$/);
// Decimal.js may use scientific notation at the edge of its supported input range.
const resultAmount = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,80})(?:\.\d{1,160})?(?:e[+-]?\d{1,3})?$/)
    .max(256);
export const acquisitionEstimateSourceSchema = z
    .object({
    url: z
        .string()
        .url()
        .max(2048)
        .refine((value) => {
        try {
            const url = new URL(value);
            return url.protocol === "https:" && !url.username && !url.password;
        }
        catch {
            return false;
        }
    }, "A public HTTPS source without credentials is required"),
    date: z.string().date(),
    description: z.string().trim().min(1).max(1000),
})
    .strict();
export const acquisitionEstimateAssumptionSchema = z
    .object({
    field: z.string().trim().min(1).max(120),
    value: z.union([z.string().trim().min(1).max(2000), z.boolean(), z.null()]),
    reason: z.string().trim().min(1).max(2000),
    source: acquisitionEstimateSourceSchema,
    impact: z.string().trim().min(1).max(2000),
})
    .strict();
/** Explicit hypotheses only. No qualification, fiscal authority or guessed credit enters this input. */
export const acquisitionEstimateCalculationInputSchema = z
    .object({
    principal: positive,
    acquisitionCharges: amount,
    quantity: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR"]),
    ownIcmsRate: fraction,
    ipiRate: fraction,
    pisRate: fraction,
    cofinsRate: fraction,
    acquisitionCredit: z.enum(["UNCONFIRMED", "INELIGIBLE"]),
    takeRate: belowOne,
    st: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("NOT_APPLICABLE"), fcpRate: fraction }).strict(),
        z
            .object({
            kind: z.literal("APPLICABLE"),
            originalMva: amount,
            internalRate: belowOne,
            adjustment: z.enum(["INTERSTATE", "ORIGINAL"]),
            fcpRate: fraction,
            additionalStBase: amount,
        })
            .strict(),
    ]),
})
    .strict();
export const acquisitionEstimateScenarioInputSchema = z
    .object({
    id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/),
    label: z.string().trim().min(1).max(200),
    assumptions: z.array(acquisitionEstimateAssumptionSchema).min(1).max(30).optional(),
    inputs: acquisitionEstimateCalculationInputSchema,
    evidence: z.array(acquisitionEstimateSourceSchema).min(1).max(30),
})
    .strict();
const base = {
    version: z.literal("ACQUISITION_ESTIMATE_V1"),
    calculationDate: z.string().date(),
    assumptions: z.array(acquisitionEstimateAssumptionSchema).min(1).max(30),
};
function compare(left, right) {
    if (!resultAmount.safeParse(left).success || !resultAmount.safeParse(right).success)
        return Number.NaN;
    const parts = (value) => {
        const [mantissa = "0", exponent = "0"] = value.toLowerCase().split("e");
        const [whole = "0", fractional = ""] = mantissa.split(".");
        return { coefficient: BigInt(whole + fractional), scale: fractional.length - Number(exponent) };
    };
    const a = parts(left);
    const b = parts(right);
    const scale = Math.max(a.scale, b.scale);
    const ac = a.coefficient * 10n ** BigInt(scale - a.scale);
    const bc = b.coefficient * 10n ** BigInt(scale - b.scale);
    return ac < bc ? -1 : ac > bc ? 1 : 0;
}
function validateCommon(value, context) {
    const first = value.scenarios[0];
    const seen = new Set();
    for (const [index, scenario] of value.scenarios.entries()) {
        if (seen.has(scenario.id))
            context.addIssue({
                code: "custom",
                path: ["scenarios", index, "id"],
                message: "Scenario IDs must be unique",
            });
        seen.add(scenario.id);
        if (first)
            for (const field of ["principal", "quantity", "takeRate", "supplierType"]) {
                const same = field === "principal" || field === "takeRate"
                    ? compare(scenario.inputs[field], first.inputs[field]) === 0
                    : scenario.inputs[field] === first.inputs[field];
                if (!same)
                    context.addIssue({
                        code: "custom",
                        path: ["scenarios", index, "inputs", field],
                        message: "Commercial inputs must be identical across scenarios",
                    });
            }
        for (const [sourceIndex, source] of scenario.evidence.entries())
            if (source.date > value.calculationDate)
                context.addIssue({
                    code: "custom",
                    path: ["scenarios", index, "evidence", sourceIndex, "date"],
                    message: "A source cannot postdate the estimate",
                });
        for (const [assumptionIndex, assumption] of (scenario.assumptions ?? []).entries())
            if (assumption.source.date > value.calculationDate)
                context.addIssue({
                    code: "custom",
                    path: ["scenarios", index, "assumptions", assumptionIndex, "source", "date"],
                    message: "An assumption source cannot postdate the estimate",
                });
    }
    for (const [index, assumption] of value.assumptions.entries())
        if (assumption.source.date > value.calculationDate)
            context.addIssue({
                code: "custom",
                path: ["assumptions", index, "source", "date"],
                message: "An assumption source cannot postdate the estimate",
            });
}
export const acquisitionEstimateInputSchema = z
    .object({ ...base, scenarios: z.array(acquisitionEstimateScenarioInputSchema).min(2).max(8) })
    .strict()
    .superRefine(validateCommon);
const unit = z
    .object({
    principal: resultAmount,
    acquisitionCharges: resultAmount,
    ownIcms: resultAmount,
    pisCofinsBase: resultAmount,
    pis: resultAmount,
    cofins: resultAmount,
    contributionCredit: resultAmount.nullable(),
    ipi: resultAmount,
    originalMva: resultAmount.nullable(),
    appliedMva: resultAmount.nullable(),
    stBase: resultAmount,
    icmsSt: resultAmount,
    fcp: resultAmount,
    acquisitionCost: resultAmount,
    netCostAfterContributionCredit: resultAmount.nullable(),
    takeAmount: resultAmount,
    listingPrice: resultAmount,
})
    .strict();
/** Mathematical evaluation of a hypothesis, never an acquisition qualification. */
export const acquisitionEstimateMathResultSchema = z
    .object({
    status: z.literal("CALCULATED"),
    acquisitionCredit: z.enum(["UNCONFIRMED", "INELIGIBLE"]),
    precision: z
        .object({ significantDigits: z.literal(40), intermediateCurrencyRounding: z.literal(false) })
        .strict(),
    unit,
    line: unit,
    memory: z
        .array(z
        .object({
        code: z.string().min(1).max(120),
        formula: z.string().min(1).max(1000),
        inputs: z.record(z.string(), z.string().max(256)),
        result: resultAmount,
    })
        .strict())
        .min(1)
        .max(30),
})
    .strict();
export const acquisitionEstimateMoneyRangeSchema = z
    .object({ min: resultAmount, max: resultAmount })
    .strict();
const scenarioResult = acquisitionEstimateScenarioInputSchema.extend({
    result: acquisitionEstimateMathResultSchema,
});
export const acquisitionEstimateSchema = z
    .object({
    ...base,
    authoritative: z.literal(false),
    status: z.literal("ESTIMATED"),
    selectedScenarioId: z.string().min(1).max(80),
    scenarios: z.array(scenarioResult).min(2).max(8),
    range: z
        .object({
        unitCost: acquisitionEstimateMoneyRangeSchema,
        listingPrice: acquisitionEstimateMoneyRangeSchema,
        lineCost: acquisitionEstimateMoneyRangeSchema,
        lineListingPrice: acquisitionEstimateMoneyRangeSchema,
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    validateCommon(value, context);
    let selected = value.scenarios[0];
    for (const [index, scenario] of value.scenarios.entries()) {
        if (selected) {
            const order = compare(scenario.result.unit.listingPrice, selected.result.unit.listingPrice);
            if (order > 0 || (order === 0 && scenario.id < selected.id))
                selected = scenario;
        }
        if (scenario.result.acquisitionCredit !== scenario.inputs.acquisitionCredit)
            context.addIssue({
                code: "custom",
                path: ["scenarios", index, "result", "acquisitionCredit"],
                message: "The mathematical result must preserve the declared credit status",
            });
        for (const level of ["unit", "line"]) {
            const amounts = scenario.result[level];
            if (scenario.inputs.acquisitionCredit === "UNCONFIRMED"
                ? amounts.contributionCredit !== null || amounts.netCostAfterContributionCredit !== null
                : amounts.contributionCredit === null ||
                    compare(amounts.contributionCredit, "0") !== 0 ||
                    amounts.netCostAfterContributionCredit === null ||
                    compare(amounts.netCostAfterContributionCredit, amounts.acquisitionCost) !== 0)
                context.addIssue({
                    code: "custom",
                    path: ["scenarios", index, "result", level],
                    message: "An estimate cannot assume contribution credits",
                });
        }
    }
    if (selected?.id !== value.selectedScenarioId)
        context.addIssue({
            code: "custom",
            path: ["selectedScenarioId"],
            message: "Select the greatest exact listing price; break equal-price ties by scenario ID",
        });
    for (const [key, level, field] of [
        ["unitCost", "unit", "acquisitionCost"],
        ["listingPrice", "unit", "listingPrice"],
        ["lineCost", "line", "acquisitionCost"],
        ["lineListingPrice", "line", "listingPrice"],
    ]) {
        const ordered = value.scenarios
            .map((scenario) => scenario.result[level][field])
            .sort(compare);
        const min = ordered[0];
        const max = ordered.at(-1);
        if (min === undefined ||
            max === undefined ||
            compare(value.range[key].min, min) !== 0 ||
            compare(value.range[key].max, max) !== 0)
            context.addIssue({
                code: "custom",
                path: ["range", key],
                message: "The range must include the exact minimum and maximum scenario results",
            });
    }
});
export const acquisitionEstimateResultSchema = acquisitionEstimateSchema;
//# sourceMappingURL=acquisition-estimate.js.map